// Auto-pull goalie — pull the trailing team's goalie in the last 60s.
//
// Owns:   the rule that pulls the trailing team's goalie automatically
//         when the last minute of regulation is ticking down and the
//         deficit is recoverable (1–2 goals).
// Reads:  state.clock (period 3, remaining ticks), state.score,
//         state.goalies, state.control, tuning.clock.
// Mutates: goalie.pulled / pos / vel for the trailing team's goalie,
//          state.control[trailing].tactic = "aggressive".
// Emits:  no events (the pull state is what the renderer / audio key
//         off of).
//
// Idempotent: once pulled, this is a no-op until the score / phase
// changes. There is no automatic un-pull — if the trailing team ties or
// takes the lead, the goalie stays out. The user can still manually
// toggle (`GoaliePullToggle`).
//
// Zero-allocation.

import { tuning } from "../state/tuning.js";
import type { MatchState } from "../state/types.js";

export function applyAutoPullGoalie(state: MatchState): void {
  if (state.clock.phase !== "Play") return;
  if (state.clock.periodIndex !== 2) return; // only period 3
  if (state.clock.remainingTicks > tuning.clock.autoPullThresholdTicks) return;

  const homeBehind = state.score.home < state.score.away;
  const awayBehind = state.score.away < state.score.home;
  if (!homeBehind && !awayBehind) return; // tied — no pull
  const diff = Math.abs(state.score.home - state.score.away);
  if (diff > 2) return; // out of reach for an empty-netter swing

  const trailingTeam = homeBehind ? "home" : "away";
  const goalie = state.goalies.find((g) => g.teamId === trailingTeam);
  if (goalie === undefined) return;
  if (goalie.pulled) return; // already pulled this period

  goalie.pulled = true;
  const halfL = tuning.rink.length / 2;
  const sign = goalie.teamId === "home" ? -1 : 1;
  goalie.pos.x = sign * (halfL + 0.5);
  goalie.pos.y = 0;
  goalie.vel.x = 0;
  goalie.vel.y = 0;

  const team = state.control.find((t) => t.teamId === trailingTeam);
  if (team !== undefined) team.tactic = "aggressive";
}
